/**
 * Done. CPD — Download utilities for calendar and plan export.
 * Ported from Jo's downloads.ts.
 */

import { createIcsCalendar } from './calendar';
import type { CalendarEvent, StructuredPlan } from './types';

function triggerDownload(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadIcsCalendar(events: CalendarEvent[], reminders: string[] = [], filename = 'done-cpd-plan.ics'): void {
  triggerDownload(filename, createIcsCalendar(events, reminders), 'text/calendar;charset=utf-8');
}

export function downloadSingleIcs(event: CalendarEvent): void {
  triggerDownload(`${event.module.id}.ics`, event.icsContent, 'text/calendar;charset=utf-8');
}

export function downloadAnnualPlan(plan: StructuredPlan, year: number): void {
  const schedule = Object.entries(plan.modulesByMonth)
    .map(([month, modules]) => {
      const moduleText = modules
        .map(m =>
          [
            `- ${m.title}`,
            `  Topic area: ${m.topicArea}`,
            `  Format: ${m.format}`,
            `  Estimated time: ${m.estimatedMinutes} minutes`,
            `  Intended outcome: ${m.intendedOutcome}`,
            `  Reflection: ${m.reflectionPrompts.join(' / ')}`,
            `  Evidence: ${m.evidenceOutput}`,
          ].join('\n'),
        )
        .join('\n');
      return `${month}\n${moduleText}`;
    })
    .join('\n\n');

  const content = [
    `Done. CPD — Annual CPD Plan ${year}`,
    '',
    'PROFESSIONAL PROFILE SUMMARY',
    plan.profileSummary,
    '',
    'ANNUAL CPD RATIONALE',
    plan.rationale,
    '',
    'ANNUAL CPD OBJECTIVES',
    ...plan.objectives.map((o, i) => `${i + 1}. ${o}`),
    '',
    'SUGGESTED ANNUAL CPD SCHEDULE',
    schedule,
    '',
    'END-OF-YEAR REVIEW REMINDER',
    plan.endOfYearReview,
  ].join('\n');

  triggerDownload(`done-cpd-annual-plan-${year}.txt`, content, 'text/plain;charset=utf-8');
}
